import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import { CHECKOUT } from "@/constants/testIds";

const sym = { INR: "₹", USD: "$" };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function slabPerTest(plan, qty) {
  if (!plan) return 0;
  if (plan.type === "individual") return plan.amount;
  if (plan.slabs) {
    for (const s of plan.slabs) if (s.upTo == null || qty <= s.upTo) return s.amount;
    return plan.slabs[plan.slabs.length - 1].amount;
  }
  return plan.amount;
}

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState(params.get("plan") || "individual_inr");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { testIds, email }

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const current = plans?.[plan];
  const isInstitution = current?.type === "institution";
  const effectiveQty = isInstitution ? Math.max(1, Number(qty) || 1) : 1;
  const per = useMemo(() => slabPerTest(current, effectiveQty), [current, effectiveQty]);
  const total = per * effectiveQty;

  const pay = async () => {
    if (!name.trim() || !email.trim()) { setError("Please enter your name and email."); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/payments/create-order", {
        plan, quantity: effectiveQty, name: name.trim(), email: email.trim(), phone: phone.trim(),
        institution: isInstitution ? name.trim() : "",
      });
      const ok = await loadRazorpay();
      if (!ok) { setError("Could not load the payment gateway. Please retry."); setLoading(false); return; }
      const rzp = new window.Razorpay({
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpayOrderId,
        name: "MSI Scale",
        description: `${current.label} · ${effectiveQty} test${effectiveQty > 1 ? "s" : ""}`,
        prefill: { name: data.name, email: data.email, contact: data.phone },
        theme: { color: "#1D4ED8" },
        handler: async (resp) => {
          try {
            const v = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({ testIds: v.data.testIds, email: v.data.email });
            toast.success("Payment successful! Your Test ID(s) have been emailed.");
          } catch (e) {
            setError(apiError(e, "Payment could not be verified."));
          } finally { setLoading(false); }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.on("payment.failed", (r) => { setError(r?.error?.description || "Payment failed."); setLoading(false); });
      rzp.open();
    } catch (e) {
      setError(apiError(e));
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteNav />
        <section className="max-w-xl mx-auto px-5 py-20 stagger">
          <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center mb-5"><CheckCircle2 className="w-7 h-7 text-emerald-600" /></div>
          <h1 className="text-3xl font-bold tracking-tight">Payment successful</h1>
          <p className="text-slate-500 mt-3">We've emailed your single-use Test ID{success.testIds.length > 1 ? "s" : ""} to <b>{success.email}</b>. Each works for either MSIS or MSOS, once.</p>
          <div data-testid={CHECKOUT.success} className="mt-6 space-y-2">
            {success.testIds.map((t) => (
              <div key={t} data-testid={CHECKOUT.testIdItem} className="flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-slate-200 font-mono font-semibold text-blue-700">
                {t}
                <button onClick={() => { navigator.clipboard.writeText(t); toast.success("Copied"); }} className="text-slate-400 hover:text-slate-700"><Copy className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate("/take-test")} className="mt-8 h-12 px-7 rounded-full bg-blue-700 hover:bg-blue-800 text-white">
            Take the Test <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav />
      <section className="max-w-3xl mx-auto px-5 py-14">
        <p className="eyebrow">Checkout</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Complete your subscription.</h1>

        <div className="grid md:grid-cols-5 gap-8 mt-10">
          <div className="md:col-span-3 space-y-4">
            <div>
              <Label className="mb-1.5 block">Plan</Label>
              <Select value={plan} onValueChange={(v) => setPlan(v)}>
                <SelectTrigger data-testid={CHECKOUT.planSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans && Object.keys(plans).map((k) => <SelectItem key={k} value={k}>{plans[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isInstitution && (
              <div>
                <Label className="mb-1.5 block">Number of tests</Label>
                <Input data-testid={CHECKOUT.quantity} type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">{isInstitution ? "Institution / Contact name" : "Full name"}</Label>
              <Input data-testid={CHECKOUT.name} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1.5 block">Email (Test ID will be sent here)</Label>
              <Input data-testid={CHECKOUT.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone (optional)</Label>
              <Input data-testid={CHECKOUT.phone} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            {error && <p data-testid={CHECKOUT.error} className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="md:col-span-2">
            <div className="rounded-xl bg-white border border-slate-200 p-6 sticky top-24">
              <p className="eyebrow">Order summary</p>
              {current && (
                <>
                  <div className="mt-3 text-sm text-slate-600">{current.label}</div>
                  <div className="mt-2 flex justify-between text-sm"><span>Per test</span><span>{sym[current.currency]}{per}</span></div>
                  <div className="mt-1 flex justify-between text-sm"><span>Quantity</span><span>{effectiveQty}</span></div>
                  <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-end">
                    <span className="font-semibold">Total</span>
                    <span className="text-2xl font-bold">{sym[current.currency]}{total}</span>
                  </div>
                </>
              )}
              <Button data-testid={CHECKOUT.payButton} onClick={pay} disabled={loading}
                className="w-full mt-6 h-12 rounded-full bg-blue-700 hover:bg-blue-800 text-white">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Pay {current ? `${sym[current.currency]}${total}` : ""}
              </Button>
              <p className="text-xs text-slate-400 mt-3 text-center">Secured by Razorpay</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-8">Already have a Test ID? <Link to="/take-test" className="text-blue-700 font-medium underline">Take the test</Link>.</p>
      </section>
    </div>
  );
}
